/**
 * The theme: light, dark or the device's setting (the default). The choice
 * is kept per device in localStorage; the pre-paint script in nuxt.config.ts
 * applies it before the first paint, and from then on this keeps the `dark`
 * class on <html> in step with the choice, the device, and other tabs.
 */
import { computed, readonly, ref, watch } from 'vue'
import {
  LEGACY_THEME_STORAGE_KEY,
  nextThemeMode,
  parseThemeMode,
  THEME_STORAGE_KEY,
  themeIsDark,
  type ThemeMode,
} from '~/utils/themeMode'

const mode = ref<ThemeMode>('system')
const systemDark = ref(false)
const isDark = computed(() => themeIsDark(mode.value, systemDark.value))
let started = false

/** Once, from plugins/theme.client.ts */
export function startThemeMode() {
  if (started) return
  started = true
  try {
    localStorage.removeItem(LEGACY_THEME_STORAGE_KEY)
    mode.value = parseThemeMode(localStorage.getItem(THEME_STORAGE_KEY))
  }
  catch { /* storage blocked: follow the device */ }

  const query = window.matchMedia('(prefers-color-scheme: dark)')
  systemDark.value = query.matches
  query.addEventListener('change', (event) => {
    systemDark.value = event.matches
  })
  window.addEventListener('storage', (event) => {
    if (event.key === THEME_STORAGE_KEY) mode.value = parseThemeMode(event.newValue)
  })

  watch(isDark, dark => document.documentElement.classList.toggle('dark', dark), { immediate: true })
}

export function useThemeMode() {
  const setMode = (next: ThemeMode) => {
    mode.value = next
    try {
      if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
      else localStorage.setItem(THEME_STORAGE_KEY, next)
    }
    catch { /* storage blocked: the choice lasts until the page reloads */ }
  }

  return {
    mode: readonly(mode),
    isDark,
    setMode,
    cycle: () => setMode(nextThemeMode(mode.value)),
  }
}
